import React, { useState } from "react";
import { publicImageUrl, driveFileUrl } from "../api/drive";
import { useT } from "../i18n";

interface PictureModalProps {
  fileIds?: string[];
  onClose: () => void;
}

export default function PictureModal({ fileIds = [], onClose }: PictureModalProps) {
  const t = useT();
  const ids = fileIds.filter(Boolean);
  const [size, setSize] = useState(160);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 border-b px-4 py-2">
          <h2 className="font-bold text-gray-800 flex items-center gap-2">
            <i className="fa-solid fa-images text-gray-500" aria-hidden="true" />
            {t("pictures.title", { count: String(ids.length) })}
          </h2>
          <div className="flex items-center gap-2">
            {ids.length > 0 && (
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <i className="fa-solid fa-magnifying-glass-minus text-xs" aria-hidden="true" />
                <input
                  type="range"
                  min={80}
                  max={360}
                  step={20}
                  value={size}
                  onChange={(e) => setSize(parseInt(e.target.value, 10))}
                  title={t("pictures.size")}
                  aria-label={t("pictures.size")}
                />
                <i className="fa-solid fa-magnifying-glass-plus text-xs" aria-hidden="true" />
              </label>
            )}
            <button
              className="inline-flex items-center gap-2 bg-gray-600 text-white rounded-md px-3 py-2 hover:bg-gray-700 transition-colors"
              onClick={onClose}
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
              <span>{t("pictures.close")}</span>
            </button>
          </div>
        </div>

        <div className="p-4 overflow-y-auto">
          {ids.length === 0 ? (
            <p className="text-sm text-gray-500">{t("pictures.empty")}</p>
          ) : (
            <div
              className="grid gap-3"
              style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${size}px, 1fr))` }}
            >
              {ids.map((fid) => (
                <a
                  key={fid}
                  href={driveFileUrl(fid)}
                  target="_blank"
                  rel="noreferrer"
                  className="block border rounded-md overflow-hidden hover:ring-2 hover:ring-indigo-300"
                >
                  <img
                    src={publicImageUrl(fid, 400)}
                    alt={fid}
                    loading="lazy"
                    className="w-full object-cover bg-gray-100"
                    style={{ height: `${Math.round(size * 0.75)}px` }}
                  />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
